import { useEffect, useState } from "react";
import { Col, Form, Modal, OverlayTrigger, Popover, Row } from "react-bootstrap";
import AddTraspButton from "../../../firstTimeUserExperience/commonCom/AddTraspButton";
import { GetImages } from "../../../../utils/GetImages";
import { CommunicationService, IntegrationsService } from "@propertelligent/client-api";
import { useNavigate } from "react-router";
import { currentUrl } from "../../../../utils/helper";
import { isElectron, desktop } from "../../../../utils/electron/desktop";
import { PubSub, TopicsEnum, NotificationType } from "../../../../utils/pubsub";
import SimpleButton from "../../../../newComponents/buttons/Simple-button";
import {
  saveSmtpSettings,
  testSmtpConnection,
  getMicrosoftClientId,
  PersonalSmtpSettingsRequest,
} from "../personalEmailApi";
import { buildMicrosoftOAuthRedirect, OUTLOOK_INBOX_SCOPES } from "../../../../utils/microsoftOAuth";

// Defaults match the most common real-world setup (e.g. Gmail): SMTP on 587 uses STARTTLS (no SSL checkbox),
// POP3 on 995 requires implicit SSL. These are two independent flags -- see PersonalSmtpSettingsBM.cs (backend)
// for why a single shared "Use SSL" checkbox is wrong here.
const EMPTY_SMTP_FORM: PersonalSmtpSettingsRequest = {
  smtpHost: "",
  smtpPort: 587,
  popHost: "",
  popPort: 995,
  username: "",
  password: "",
  useSmtpSsl: false,
  usePopSsl: true,
  fromName: "",
  fromEmail: "",
};

// Pure so it's unit-testable without mounting the component. Mirrors the backend's SaveSmtpSettingsAsync/
// TestConnectionAsync validation — keeps Test/Connect disabled until the form can't fail on an empty-field
// validation error.
export const isSmtpFormValid = (form: PersonalSmtpSettingsRequest): boolean =>
  form.smtpHost.trim() !== "" &&
  form.smtpPort > 0 &&
  form.popHost.trim() !== "" &&
  form.popPort > 0 &&
  form.username.trim() !== "" &&
  form.password.trim() !== "" &&
  form.fromEmail.trim() !== "";

// Org-Admin-only? No — this is the PERSONAL connect screen (any org user). Three mutually-exclusive provider
// options: Gmail (OAuth), Outlook (OAuth, send-only in this phase), or a custom SMTP/POP3 account (send + basic
// read-only inbox — see .claude/context/emails.md Rule 12's Phase 2 tracked-exception note for why POP3 support
// is deliberately thin). `allowSmtp`/`allowOutlook` both default to false so the other mount point
// (tenants/SendReminder.tsx, a Gmail-only "send a reminder" widget with no providerType plumbing) keeps its
// original Gmail-only behavior — connecting Outlook there would silently break that widget, since it always
// sends via the Gmail-shaped postApiGmailSendEmailMessage call regardless of which provider is actually
// connected. Only the personal Messaging > Email screen (emailview.tsx), which does have that plumbing, opts in.
const AddGmailAccount = ({ border, onConnected, allowSmtp = false, allowOutlook = false }: { border: boolean; onConnected?: () => void; allowSmtp?: boolean; allowOutlook?: boolean }) => {
  const navigate = useNavigate();
  const [clientId, setClientId] = useState("");
  const [microsoftClientId, setMicrosoftClientId] = useState("");
  const [refreshtoken, setRefreshToken] = useState<Boolean>();
  const [showSmtpForm, setShowSmtpForm] = useState(false);
  const [smtpForm, setSmtpForm] = useState<PersonalSmtpSettingsRequest>(EMPTY_SMTP_FORM);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    IntegrationsService.postApiIntegrationsKeysGmailClientId()
      .then((res) => {
        setClientId(res?.success)
      })
      .catch((error) => {
        console.error(error)
      })

    CommunicationService.getApiGmailHasRefreshToken()
      .then((res) => {
        setRefreshToken(res?.success)
      }).catch((error) => {
        console.error(error)
      })

  }, [refreshtoken, clientId,])

  // Mount-only, and only when the Outlook option is actually rendered (see allowOutlook) — avoids an
  // unnecessary request from the Gmail-only tenants/SendReminder.tsx mount point, and avoids refetching
  // every time the Gmail effect above settles (refreshtoken/clientId are not relevant to this call).
  useEffect(() => {
    if (!allowOutlook) return;
    getMicrosoftClientId().then(setMicrosoftClientId);
  }, [allowOutlook])


  async function oauthSignIn() {
    var oauth2Endpoint = 'https://accounts.google.com/o/oauth2/v2/auth';
    var scopes = "https://mail.google.com/ https://www.googleapis.com/auth/gmail.addons.current.message.action https://www.googleapis.com/auth/gmail.addons.current.message.readonly https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/contacts.readonly https://www.googleapis.com/auth/userinfo.profile";
    var params = {
      'client_id': `${clientId}`,
      redirect_uri: `${currentUrl}/callback/gmail`,
      'response_type': 'code',
      'scope': scopes,
      'include_granted_scopes': 'false',
      'state': 'pass-through value',
      'access_type': 'offline',
      'prompt': 'consent'
    };

    // Desktop: a full-page redirect to Google would navigate the app away and
    // Google blocks OAuth in embedded/custom-scheme origins. Run the consent in
    // a child window (main process), capture the auth code, and let the existing
    // /callback/gmail route complete the exchange with the current JWT.
    if (isElectron && desktop?.gmailOAuth) {
      const authUrl = `${oauth2Endpoint}?${new URLSearchParams(params as any).toString()}`;
      const res = await desktop.gmailOAuth(authUrl);
      if (res?.code) {
        navigate(`/callback/gmail?code=${encodeURIComponent(res.code)}`);
      } else if (res?.error && res.error !== "closed") {
        console.error("Gmail OAuth failed:", res.error);
      }
      return;
    }

    var form = document.createElement('form');
    form.setAttribute('method', 'GET');
    form.setAttribute('action', oauth2Endpoint);

    for (var p in params) {
      var input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', p);
      input.setAttribute('value', params[p]);
      form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();

  }

  // Mirrors oauthSignIn's electron/browser fork, but builds the auth URL via the shared
  // buildMicrosoftOAuthRedirect helper (used by both this personal screen and the org screen) instead
  // of an inline Google-shaped params object.
  async function outlookOauthSignIn() {
    if (!microsoftClientId) {
      console.error("Microsoft client id is not yet available; cannot start Outlook OAuth.");
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Failure,
        title: "Failure",
        message: "Unable to start Outlook sign-in right now. Please try again in a moment.",
      });
      return;
    }

    const { endpoint, params, authUrl } = buildMicrosoftOAuthRedirect({
      clientId: microsoftClientId,
      redirectUri: `${currentUrl}/callback/outlook`,
      scopes: OUTLOOK_INBOX_SCOPES,
    });

    // Desktop: same reasoning as Gmail's electron branch above -- run the consent in a child window and
    // let the existing /callback/outlook route complete the exchange with the current JWT. Reuses the
    // same `desktop.gmailOAuth` bridge method (it just opens a URL and captures the redirected `code`,
    // nothing Google-specific despite the method's name -- renaming it is out of scope for this phase).
    if (isElectron && desktop?.gmailOAuth) {
      const res = await desktop.gmailOAuth(authUrl);
      if (res?.code) {
        navigate(`/callback/outlook?code=${encodeURIComponent(res.code)}`);
      } else if (res?.error && res.error !== "closed") {
        console.error("Outlook OAuth failed:", res.error);
      }
      return;
    }

    const form = document.createElement("form");
    form.setAttribute("method", "GET");
    form.setAttribute("action", endpoint);

    for (const p in params) {
      const input = document.createElement("input");
      input.setAttribute("type", "hidden");
      input.setAttribute("name", p);
      input.setAttribute("value", params[p]);
      form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();
  }

  const updateSmtpField = <K extends keyof PersonalSmtpSettingsRequest>(field: K, value: PersonalSmtpSettingsRequest[K]) => {
    setSmtpForm((prev) => ({ ...prev, [field]: value }));
  };

  // Shared by the modal's X/backdrop/Esc (via onHide) and the Cancel button so no dismissal path
  // leaves stale values behind for the next time the form is opened.
  const closeSmtpForm = () => {
    setShowSmtpForm(false);
    setSmtpForm(EMPTY_SMTP_FORM);
  };

  const handleTestSmtp = () => {
    setIsTesting(true);
    testSmtpConnection(smtpForm)
      .then(() => {
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Success,
          title: "Success",
          message: "Test email sent successfully.",
        });
      })
      .catch((error) => {
        console.error("SMTP test connection failed:", error);
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Failure,
          title: "Failure",
          message: "Test failed — check your settings and try again.",
        });
      })
      .finally(() => setIsTesting(false));
  };

  const handleSaveSmtp = () => {
    setIsSaving(true);
    saveSmtpSettings(smtpForm)
      .then(() => {
        closeSmtpForm();
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Success,
          title: "Success",
          message: "SMTP account connected.",
        });
        onConnected?.();
      })
      .catch((error) => {
        console.error("Failed to save personal SMTP settings:", error);
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Failure,
          title: "Failure",
          message: "Unable to save SMTP settings. Please check the values and try again.",
        });
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <>
      <div
        className={`addGmailContainer ${!border && "border-0"}`}
        style={{ overflowX: "hidden", overflowY: "auto" }}
      >
        <div className="addGmailAccount">
          <p className="main_heading black">
            Configure your email account for sending & receiving emails{" "}
          </p>
          <img
            src={GetImages.AddEmail}
            alt="AddGmailImage"
            className="styleicon"
          />
          <div className="d-flex flex-column gap-3  ">
            <button
              type="submit"
              className="emailConnectButton"
              onClick={() => oauthSignIn()}
            >
              <img
                src={GetImages.Google}
                alt="GoogleIcon"
                className="styleicon"
              />
              <p className="normal_text fw-600 text-start emailConnectButtonLabel">Connect with gmail</p>
            </button>
            {allowOutlook && (
              <button
                type="button"
                className="emailConnectButton"
                onClick={() => outlookOauthSignIn()}
              >
                <img
                  src={GetImages.Outlook}
                  alt="OutlookIcon"
                  className="styleicon"
                />
                <p className="normal_text fw-600 text-start emailConnectButtonLabel">Connect with Outlook</p>
              </button>
            )}
            {allowSmtp && (
              <button
                type="button"
                className="emailConnectButton"
                onClick={() => setShowSmtpForm(true)}
              >
                <img
                  src={GetImages.AddEmail}
                  alt="EmailIcon"
                  height={24}
                  width={24}
                />
                <p className="normal_text fw-600 text-start emailConnectButtonLabel">Connect a Custom SMTP/POP3 Account</p>
              </button>
            )}
          </div>
        </div>

        {/* dialogClassName below overrides a global, unscoped `.modal-dialog { max-width: 100% }` rule in
            tenantStyles.css that otherwise defeats Bootstrap's `size="lg"` max-width for every modal in the app. */}
        {allowSmtp && (
        <Modal size="lg" centered dialogClassName="smtpPop3ModalDialog" show={showSmtpForm} onHide={closeSmtpForm}>
          <Modal.Header closeButton>
            <Modal.Title className="main-heading d-flex align-items-center gap-2">
              Connect a Custom SMTP/POP3 Account
              <OverlayTrigger
                trigger="click"
                placement="bottom"
                rootClose
                overlay={
                  <Popover id="smtp-pop3-setup-help" className="smtpPop3InfoPopover" style={{ maxWidth: 380 }}>
                    <Popover.Header as="h3">How to connect (Gmail example)</Popover.Header>
                    <Popover.Body>
                      <ol className="ps-3 mb-2 sub_text grey_subtext1">
                        <li className="mb-2">
                          Gmail Settings (gear icon) → <b>See all settings</b> → <b>Forwarding and POP/IMAP</b> tab →
                          under "POP Download" select <b>"Enable POP for all mail"</b> → scroll to the bottom and
                          click <b>Save Changes</b>.
                        </li>
                        <li className="mb-2">
                          If 2-Step Verification is on, create an <b>App Password</b> (Google Account → Security →
                          App Passwords) and enter it as your Password below — your regular Gmail password won't work.
                        </li>
                        <li className="mb-2">
                          Use <b>smtp.gmail.com</b> : <b>587</b> to send (leave "SMTP: use SSL" unchecked), and{" "}
                          <b>pop.gmail.com</b> : <b>995</b> to receive (check "POP3: use SSL").
                        </li>
                        <li>
                          If Test still fails, check your inbox for a Google "security alert" email and confirm it
                          was you, or visit <b>accounts.google.com/DisplayUnlockCaptcha</b> to unblock sign-in.
                        </li>
                      </ol>
                      <div className="sub_text grey_subtext1">
                        Other providers: use the SMTP/POP3 host, port, and SSL settings from your email provider's
                        documentation — the same send/receive-server split usually applies.
                      </div>
                    </Popover.Body>
                  </Popover>
                }
              >
                <img
                  src={GetImages.InfoIcon}
                  alt="Setup help"
                  className="clickable"
                  width={16}
                  height={16}
                />
              </OverlayTrigger>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3" controlId="smtpHost">
                    <Form.Label className="sub_text grey_subtext1">SMTP Server (sending)</Form.Label>
                    <Form.Control
                      className="sub_text grey_subtext1"
                      value={smtpForm.smtpHost}
                      onChange={(e) => updateSmtpField("smtpHost", e.target.value)}
                      type="text"
                      placeholder="Example: smtp.gmail.com"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3" controlId="smtpPort">
                    <Form.Label className="sub_text grey_subtext1">SMTP Port</Form.Label>
                    <Form.Control
                      className="sub_text grey_subtext1"
                      value={smtpForm.smtpPort}
                      onChange={(e) => updateSmtpField("smtpPort", Number(e.target.value))}
                      type="number"
                      placeholder="587"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3" controlId="popHost">
                    <Form.Label className="sub_text grey_subtext1">POP3 Server (receiving)</Form.Label>
                    <Form.Control
                      className="sub_text grey_subtext1"
                      value={smtpForm.popHost}
                      onChange={(e) => updateSmtpField("popHost", e.target.value)}
                      type="text"
                      placeholder="Example: pop.gmail.com"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3" controlId="popPort">
                    <Form.Label className="sub_text grey_subtext1">POP3 Port</Form.Label>
                    <Form.Control
                      className="sub_text grey_subtext1"
                      value={smtpForm.popPort}
                      onChange={(e) => updateSmtpField("popPort", Number(e.target.value))}
                      type="number"
                      placeholder="995"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3" controlId="smtpUsername">
                    <Form.Label className="sub_text grey_subtext1">Username</Form.Label>
                    <Form.Control
                      className="sub_text grey_subtext1"
                      value={smtpForm.username}
                      onChange={(e) => updateSmtpField("username", e.target.value)}
                      type="text"
                      placeholder="Enter User Name"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3" controlId="smtpPassword">
                    <Form.Label className="sub_text grey_subtext1">Password</Form.Label>
                    <Form.Control
                      className="sub_text grey_subtext1"
                      value={smtpForm.password}
                      onChange={(e) => updateSmtpField("password", e.target.value)}
                      type="password"
                      autoComplete="new-password"
                      placeholder="Enter Password"
                    />
                  </Form.Group>
                </Col>
              </Row>
              {/* Plain, self-contained markup (no react-bootstrap Form.Check / .form-check-label) -- some ambient
                  global CSS elsewhere in this app clips/overlaps .form-check-label on wrap, and rather than chase
                  that down further this sidesteps it entirely with a layout this component fully controls. */}
              <Row>
                <Col xs={12} md={6}>
                  <div className="mb-3" style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <input
                      type="checkbox"
                      id="smtpUseSsl"
                      checked={smtpForm.useSmtpSsl}
                      onChange={(e) => updateSmtpField("useSmtpSsl", e.target.checked)}
                      style={{ flexShrink: 0, marginTop: 3 }}
                    />
                    <label
                      htmlFor="smtpUseSsl"
                      className="sub_text grey_subtext1"
                      style={{
                        flex: "1 1 0%",
                        minWidth: 0,
                        maxWidth: "100%",
                        marginBottom: 0,
                        whiteSpace: "pre-wrap",
                        overflow: "visible",
                        wordBreak: "break-word",
                        textOverflow: "clip",
                        display: "block",
                      }}
                    >
                      SMTP: use SSL (typically port 465; leave unchecked for STARTTLS on port 587)
                    </label>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="mb-3" style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <input
                      type="checkbox"
                      id="popUseSsl"
                      checked={smtpForm.usePopSsl}
                      onChange={(e) => updateSmtpField("usePopSsl", e.target.checked)}
                      style={{ flexShrink: 0, marginTop: 3 }}
                    />
                    <label
                      htmlFor="popUseSsl"
                      className="sub_text grey_subtext1"
                      style={{
                        flex: "1 1 0%",
                        minWidth: 0,
                        maxWidth: "100%",
                        marginBottom: 0,
                        whiteSpace: "pre-wrap",
                        overflow: "visible",
                        wordBreak: "break-word",
                        textOverflow: "clip",
                        display: "block",
                      }}
                    >
                      POP3: use SSL (required for most providers, including Gmail, on port 995)
                    </label>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3" controlId="fromName">
                    <Form.Label className="sub_text grey_subtext1">From Name</Form.Label>
                    <Form.Control
                      className="sub_text grey_subtext1"
                      value={smtpForm.fromName}
                      onChange={(e) => updateSmtpField("fromName", e.target.value)}
                      type="text"
                      placeholder="Example: Jane Doe"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3" controlId="fromEmail">
                    <Form.Label className="sub_text grey_subtext1">From Email</Form.Label>
                    <Form.Control
                      className="sub_text grey_subtext1"
                      value={smtpForm.fromEmail}
                      onChange={(e) => updateSmtpField("fromEmail", e.target.value)}
                      type="email"
                      placeholder="Enter From Email"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer className="smtpPop3ModalFooter">
            <AddTraspButton
              type="button"
              bname="Cancel"
              onClick={closeSmtpForm}
              width="fit-content"
            />
            <SimpleButton
              label="Test"
              onClick={handleTestSmtp}
              isDisabled={!isSmtpFormValid(smtpForm)}
              isLoading={isTesting}
            />
            <SimpleButton
              label="Connect"
              onClick={handleSaveSmtp}
              isDisabled={!isSmtpFormValid(smtpForm)}
              isLoading={isSaving}
            />
          </Modal.Footer>
        </Modal>
        )}
      </div>
    </>
  );
}

export default AddGmailAccount
